import * as THREE from 'three';
import { Tween, Easing, Group } from '@tweenjs/tween.js';

interface ControlOpts {
  domElement: HTMLElement;
  camera: THREE.PerspectiveCamera;
  /** Initial look-at point. Used to compute starting yaw/pitch. */
  target: THREE.Vector3;
}

const RETURN_DURATION_MS = 950;
const TWO_PI = Math.PI * 2;

/**
 * A free-camera controller. The camera occupies a position in space and
 * has a look direction (yaw + pitch); it does NOT orbit a target.
 *
 *   • Left-drag   →  rotate the camera in place (look around)
 *   • Right-drag  →  pan: translate the camera laterally in its own
 *                    right/up plane (no rotation)
 *   • Shift+drag  →  same as right-drag
 *   • Wheel       →  dolly: translate the camera along its look direction
 *   • R / Esc     →  return-to-home (smooth tween)
 *   • Touch       →  one-finger look, two-finger pinch-dolly + pan
 *
 * State channels are damped toward eased targets, so motion feels weighted
 * and a sudden release inertia-decays calmly. No idle motion.
 */
export class OrbitControls {
  camera: THREE.PerspectiveCamera;
  domElement: HTMLElement;

  // Authoritative state (current).
  yaw: number;
  pitch: number;

  // Eased targets (input writes here; current eases toward them).
  tYaw: number;
  tPitch: number;
  tPos: THREE.Vector3;

  // Home pose.
  homePos: THREE.Vector3;
  homeYaw: number;
  homePitch: number;

  // Tunables.
  damping = 0.13;
  lookSpeed = 0.0028;        // rad per pixel (drag → rotation)
  panSpeed = 0.024;          // world units per pixel (right-drag → translation)
  dollySpeed = 0.7;          // world units per wheel notch
  minPitch = -Math.PI / 2 + 0.18;
  maxPitch = Math.PI / 2 - 0.18;
  /** Soft pan-bound: keep camera within this radius of the origin so a
   *  determined drag can't lose the canvas entirely. */
  panBoundsRadius = 32;

  enabled = true;
  onUserInteract?: () => void;

  // Tweens.
  private tweens = new Group();
  private returning = false;

  // Pointer state.
  private mode: 'look' | 'pan' | null = null;
  private lastX = 0;
  private lastY = 0;
  private touchActive: 'drag' | 'pinch' | null = null;
  private touchPinchDist = 0;
  private touchPanLast: { x: number; y: number } | null = null;

  constructor(opts: ControlOpts) {
    this.camera = opts.camera;
    this.domElement = opts.domElement;

    // Use YXZ Euler order: yaw rotates around world Y, then pitch around
    // local X. This gives intuitive look-around without roll drift.
    this.camera.rotation.order = 'YXZ';

    // Derive starting yaw/pitch from where the camera was set to look.
    const forward = new THREE.Vector3()
      .subVectors(opts.target, this.camera.position)
      .normalize();
    this.yaw = Math.atan2(-forward.x, -forward.z);
    this.pitch = Math.asin(THREE.MathUtils.clamp(forward.y, -1, 1));

    this.tYaw = this.yaw;
    this.tPitch = this.pitch;
    this.tPos = this.camera.position.clone();

    this.homeYaw = this.yaw;
    this.homePitch = this.pitch;
    this.homePos = this.camera.position.clone();

    this.applyToCamera();
    this.bindEvents();
  }

  // ─── Pose application ────────────────────────────────────────

  private applyToCamera() {
    this.camera.rotation.set(this.pitch, this.yaw, 0, 'YXZ');
    this.camera.updateMatrixWorld();
  }

  /** Camera-local axes derived from the current rotation. */
  private getAxes(): { right: THREE.Vector3; up: THREE.Vector3; forward: THREE.Vector3 } {
    this.applyToCamera();
    const m = this.camera.matrixWorld.elements;
    return {
      right:   new THREE.Vector3(m[0], m[1], m[2]),
      up:      new THREE.Vector3(m[4], m[5], m[6]),
      forward: new THREE.Vector3(-m[8], -m[9], -m[10]),
    };
  }

  // ─── Event binding ───────────────────────────────────────────

  private bindEvents() {
    const el = this.domElement;
    el.addEventListener('mousedown', this.onMouseDown);
    el.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);
    el.addEventListener('wheel', this.onWheel, { passive: false });
    el.addEventListener('touchstart', this.onTouchStart, { passive: false });
    el.addEventListener('touchmove', this.onTouchMove, { passive: false });
    el.addEventListener('touchend', this.onTouchEnd);
    window.addEventListener('keydown', this.onKey);
  }

  private onMouseDown = (e: MouseEvent) => {
    if (!this.enabled || this.returning) return;
    if ((e.target as HTMLElement)?.closest('.panel, .legend, .controls-panel, .legend-clear, .detail-panel')) return;
    if (e.button !== 0 && e.button !== 2) return;
    e.preventDefault();
    // Default drag = pan (swipe to move the canvas, like Figma/Maps).
    // Right-click or shift+drag = look (rotate the camera in place).
    const wantLook = e.button === 2 || (e.button === 0 && e.shiftKey);
    this.mode = wantLook ? 'look' : 'pan';
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.domElement.classList.add('grabbing');
    this.onUserInteract?.();
  };

  private onContextMenu = (e: MouseEvent) => {
    if ((e.target as HTMLElement)?.closest('.panel, .legend, .controls-panel, .detail-panel')) return;
    e.preventDefault();
  };

  private onMouseMove = (e: MouseEvent) => {
    if (!this.mode || this.returning) return;
    const dx = e.clientX - this.lastX;
    const dy = e.clientY - this.lastY;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    if (this.mode === 'look') {
      this.tYaw -= dx * this.lookSpeed;
      this.tPitch -= dy * this.lookSpeed;
      this.tPitch = Math.min(this.maxPitch, Math.max(this.minPitch, this.tPitch));
    } else {
      this.applyPan(dx, dy);
    }
  };

  private onMouseUp = () => {
    if (this.mode) this.domElement.classList.remove('grabbing');
    this.mode = null;
  };

  private onWheel = (e: WheelEvent) => {
    if (!this.enabled || this.returning) return;
    e.preventDefault();
    const { forward } = this.getAxes();
    const ticks = Math.min(Math.abs(e.deltaY) / 100, 3);
    const direction = -Math.sign(e.deltaY); // wheel down → move backward
    this.tPos.addScaledVector(forward, direction * ticks * this.dollySpeed);
    this.clampPos();
    this.onUserInteract?.();
  };

  private applyPan(dxPx: number, dyPx: number) {
    const { right, up } = this.getAxes();
    const k = this.panSpeed;
    this.tPos
      .addScaledVector(right, -dxPx * k)
      .addScaledVector(up, dyPx * k);
    this.clampPos();
    this.onUserInteract?.();
  }

  private clampPos() {
    if (this.tPos.length() > this.panBoundsRadius) {
      this.tPos.setLength(this.panBoundsRadius);
    }
  }

  // ─── Touch ───────────────────────────────────────────────────

  private onTouchStart = (e: TouchEvent) => {
    if (!this.enabled || this.returning) return;
    if ((e.target as HTMLElement)?.closest('.panel, .legend, .controls-panel, .legend-clear, .detail-panel')) return;
    if (e.touches.length === 1) {
      this.touchActive = 'drag';
      this.lastX = e.touches[0].clientX;
      this.lastY = e.touches[0].clientY;
      this.touchPanLast = null;
    } else if (e.touches.length === 2) {
      this.touchActive = 'pinch';
      this.touchPinchDist = this.pinchDistance(e);
      const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      this.touchPanLast = { x: cx, y: cy };
    }
    this.onUserInteract?.();
  };

  private onTouchMove = (e: TouchEvent) => {
    if (!this.touchActive || this.returning) return;
    e.preventDefault();
    if (this.touchActive === 'drag' && e.touches.length === 1) {
      // One-finger drag = pan (matches the default desktop drag).
      const dx = e.touches[0].clientX - this.lastX;
      const dy = e.touches[0].clientY - this.lastY;
      this.lastX = e.touches[0].clientX;
      this.lastY = e.touches[0].clientY;
      this.applyPan(dx, dy);
    } else if (this.touchActive === 'pinch' && e.touches.length === 2) {
      const d = this.pinchDistance(e);
      const dollyAmount = (this.touchPinchDist - d) * 0.06;
      const { forward } = this.getAxes();
      this.tPos.addScaledVector(forward, -dollyAmount);
      this.clampPos();
      this.touchPinchDist = d;

      const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      if (this.touchPanLast) {
        this.applyPan(cx - this.touchPanLast.x, cy - this.touchPanLast.y);
      }
      this.touchPanLast = { x: cx, y: cy };
    }
  };

  private onTouchEnd = () => {
    this.touchActive = null;
    this.touchPanLast = null;
  };

  private pinchDistance(e: TouchEvent): number {
    const a = e.touches[0];
    const b = e.touches[1];
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  }

  // ─── Keyboard ────────────────────────────────────────────────

  private onKey = (e: KeyboardEvent) => {
    if (!this.enabled) return;
    const tgt = e.target as HTMLElement | null;
    if (tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA')) return;
    if (e.key === 'r' || e.key === 'R' || e.key === 'Escape') {
      this.returnHome();
    }
  };

  // ─── Return-to-home ──────────────────────────────────────────

  /** Smoothly tween back to the arrival pose: home position, home yaw, home pitch. */
  returnHome() {
    if (this.returning) return;
    if (!this.isAwayFromHome()) return;

    this.returning = true;
    this.tweens.removeAll();

    const yawStart = this.yaw;
    const pitchStart = this.pitch;
    const posStart = this.camera.position.clone();

    // Take the shortest angular path on yaw — going backwards across
    // the discontinuity reads as a violent unwind otherwise.
    let dYaw = this.homeYaw - yawStart;
    while (dYaw > Math.PI) dYaw -= TWO_PI;
    while (dYaw < -Math.PI) dYaw += TWO_PI;
    const yawEnd = yawStart + dYaw;

    new Tween({ t: 0 }, this.tweens)
      .to({ t: 1 }, RETURN_DURATION_MS)
      .easing(Easing.Cubic.Out)
      .onUpdate(({ t }) => {
        this.yaw = yawStart + (yawEnd - yawStart) * t;
        this.pitch = pitchStart + (this.homePitch - pitchStart) * t;
        this.tYaw = this.yaw;
        this.tPitch = this.pitch;
        this.tPos.lerpVectors(posStart, this.homePos, t);
        this.camera.position.copy(this.tPos);
      })
      .onComplete(() => {
        this.yaw = this.homeYaw;
        this.pitch = this.homePitch;
        this.tYaw = this.yaw;
        this.tPitch = this.pitch;
        this.tPos.copy(this.homePos);
        this.camera.position.copy(this.homePos);
        this.returning = false;
      })
      .start();
  }

  isAwayFromHome(): boolean {
    if (this.returning) return true;
    const dPos = this.camera.position.distanceTo(this.homePos);
    let dYaw = ((this.yaw - this.homeYaw) % TWO_PI + TWO_PI) % TWO_PI;
    if (dYaw > Math.PI) dYaw = TWO_PI - dYaw;
    const dPitch = Math.abs(this.pitch - this.homePitch);
    return dPos > 0.30 || dYaw > 0.04 || dPitch > 0.04;
  }

  isInteracting(): boolean {
    return this.mode !== null || this.touchActive !== null;
  }

  update() {
    this.tweens.update(performance.now());
    if (!this.returning) {
      this.yaw += (this.tYaw - this.yaw) * this.damping;
      this.pitch += (this.tPitch - this.pitch) * this.damping;
      this.camera.position.x += (this.tPos.x - this.camera.position.x) * this.damping;
      this.camera.position.y += (this.tPos.y - this.camera.position.y) * this.damping;
      this.camera.position.z += (this.tPos.z - this.camera.position.z) * this.damping;
    }
    this.applyToCamera();
  }

  dispose() {
    this.tweens.removeAll();
  }
}
